// Route errors and unknown paths. Errors are reported (Sentry, when on) and never shown raw to the user.
import { Link, type ErrorComponentProps } from '@tanstack/react-router';
import { Component, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { failureOf } from '../lib/queries.ts';
import { captureError } from '../lib/sentry.ts';
import { TechDetails } from './TechDetails.tsx';

export function ErrorScreen({ error }: ErrorComponentProps) {
  const { t } = useTranslation();
  useEffect(() => captureError(error), [error]);
  return (
    <div role="alert" className="flex max-w-xl flex-col gap-2 p-8">
      <h1 className="text-h1 font-bold">{t('error.title')}</h1>
      <p className="text-body">{t('error.body')}</p>
      {failureOf(error) && <TechDetails diag={failureOf(error)!.diag} />}
      <button type="button" onClick={() => window.location.reload()} className="w-fit rounded-button bg-brand px-4 py-2 text-sm font-semibold text-white">
        {t('error.retry')}
      </button>
    </div>
  );
}

export function NotFound() {
  return (
    <div className="p-8">
      <h1 className="text-h1 font-bold">Página não encontrada</h1>
      <Link to="/">Voltar para Hoje</Link>
    </div>
  );
}

/** Last line of defence outside the router (a crash while rendering the providers themselves). */
export class RootErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: unknown) {
    captureError(error);
  }
  override render() {
    return this.state.failed ? <p className="p-8">Algo deu errado. Recarregue a página.</p> : this.props.children;
  }
}
